import { useState, useEffect } from "react";
import { X, Activity, Droplet, Heart, TrendingUp, Brain, Smile } from "lucide-react";
import VitalsChart from "./VitalsChart";

interface PatientAnalyticsProps {
  patientName: string;
  vitalsHistory: any[];
  labHistory: any[];
  notesHistory?: any[];
  onClose: () => void;
}

export default function PatientAnalytics({ patientName, vitalsHistory, labHistory, notesHistory, onClose }: PatientAnalyticsProps) {
  // Extract BP history
  const bpData = vitalsHistory
    .filter(v => v.bloodPressure)
    .map(v => {
      const [sys, dia] = v.bloodPressure.split('/').map(Number);
      return {
        date: new Date(v.recordedAt).toLocaleDateString("en-GB"),
        sys: sys || 0,
        dia: dia || 0,
        full: v.bloodPressure
      };
    });

  // Extract Fasting Blood Sugar (FBS) or Random Blood Sugar (RBS)
  const rbsData = labHistory
    .filter(l => l.testName?.toUpperCase().includes("RBS") || l.testName?.toUpperCase().includes("FBS") || l.testName?.toUpperCase().includes("SUGAR"))
    .map(l => ({
      date: new Date(l.testDate).toLocaleDateString("en-GB"),
      value: parseFloat(l.result) || 0,
      name: l.testName
    }));

  // Extract DLQI from notesHistory
  const dlqiData = (notesHistory || [])
    .filter(r => r.gpNotes?.includes("DLQI Score:"))
    .map(r => {
      const match = r.gpNotes.match(/DLQI Score:\s*(\d+)/i);
      if (!match) return null;
      return {
        date: new Date(r.createdAt).toLocaleDateString("en-GB"),
        value: parseInt(match[1]),
        full: match[0]
      };
    })
    .filter(Boolean);

  // Extract PHQ-9 from notesHistory
  const phq9Data = (notesHistory || [])
    .filter(r => r.gpNotes?.includes("PHQ-9 Score:"))
    .map(r => {
      const match = r.gpNotes.match(/PHQ-9 Score:\s*(\d+)/i);
      if (!match) return null;
      return {
        date: new Date(r.createdAt).toLocaleDateString("en-GB"),
        value: parseInt(match[1]),
        full: match[0]
      };
    })
    .filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-200">
        
        {/* Header */}
        <div className="bg-indigo-950 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-800/50 p-2 rounded-lg border border-indigo-700">
              <TrendingUp className="w-5 h-5 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white tracking-wider flex items-center gap-2">
                التحليلات البيانية السريرية
              </h2>
              <p className="text-[10px] text-indigo-300 font-mono tracking-widest uppercase">
                Clinical Analytics & Trends: {patientName}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-indigo-300 hover:text-white hover:bg-rose-500 rounded-xl transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weight & BMI Trend */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Activity className="w-5 h-5 text-blue-500" />
                <h3 className="font-bold text-slate-700">مؤشر كتلة الجسم والوزن (Weight & BMI)</h3>
              </div>
              <VitalsChart vitals={vitalsHistory} />
            </div>

            {/* Blood Pressure Trend */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Heart className="w-5 h-5 text-rose-500" />
                <h3 className="font-bold text-slate-700">معدل ضغط الدم (Blood Pressure)</h3>
              </div>
              {bpData.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {bpData.map((b, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-100 p-3 rounded-lg">
                      <span className="text-xs font-bold text-slate-500">{b.date}</span>
                      <span className={`text-lg font-black ${b.sys > 140 || b.dia > 90 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {b.full} <span className="text-[10px] text-slate-400 font-medium">mmHg</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-slate-400 text-xs py-12 bg-slate-50 rounded-lg border border-slate-100 italic">
                  لا توجد قراءات لضغط الدم.
                </div>
              )}
            </div>

            {/* Blood Sugar Trend */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm lg:col-span-2">
              <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Droplet className="w-5 h-5 text-teal-500" />
                <h3 className="font-bold text-slate-700">مستويات السكر في الدم (Blood Sugar Trends)</h3>
              </div>
              {rbsData.length > 0 ? (
                <div className="flex flex-wrap gap-4">
                  {rbsData.map((b, i) => (
                    <div key={i} className="flex-1 min-w-[200px] flex items-center justify-between bg-slate-50 border border-slate-100 p-4 rounded-lg">
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 block mb-1">{b.name}</span>
                        <span className="text-xs font-bold text-slate-600">{b.date}</span>
                      </div>
                      <span className={`text-2xl font-black ${b.value > 140 ? 'text-amber-600' : 'text-teal-600'}`}>
                        {b.value} <span className="text-[10px] text-slate-400 font-medium">mg/dL</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-slate-400 text-xs py-12 bg-slate-50 rounded-lg border border-slate-100 italic">
                  لا توجد سجلات لتحاليل السكر.
                </div>
              )}
            </div>
            {/* DLQI Trend */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Smile className="w-5 h-5 text-purple-500" />
                <h3 className="font-bold text-slate-700">جودة الحياة للأمراض الجلدية (DLQI)</h3>
              </div>
              {dlqiData.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {dlqiData.map((b: any, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-100 p-3 rounded-lg">
                      <span className="text-xs font-bold text-slate-500">{b.date}</span>
                      <span className={`text-lg font-black ${b.value > 10 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {b.value} <span className="text-[10px] text-slate-400 font-medium">/ 30</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-slate-400 text-xs py-12 bg-slate-50 rounded-lg border border-slate-100 italic">
                  لا توجد قراءات مسجلة لمقياس DLQI.
                </div>
              )}
            </div>

            {/* PHQ-9 Trend */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Brain className="w-5 h-5 text-indigo-500" />
                <h3 className="font-bold text-slate-700">مقياس صحة المريض للاكتئاب (PHQ-9)</h3>
              </div>
              {phq9Data.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {phq9Data.map((b: any, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-100 p-3 rounded-lg">
                      <span className="text-xs font-bold text-slate-500">{b.date}</span>
                      <span className={`text-lg font-black ${b.value > 14 ? 'text-rose-600' : (b.value > 9 ? 'text-amber-500' : 'text-emerald-600')}`}>
                        {b.value} <span className="text-[10px] text-slate-400 font-medium">/ 27</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-slate-400 text-xs py-12 bg-slate-50 rounded-lg border border-slate-100 italic">
                  لا توجد قراءات مسجلة لمقياس PHQ-9.
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
